import { TCSSMode } from './types';

// Sass.js CDN URL
const SASS_CDN = 'https://cdn.jsdelivr.net/npm/sass.js@0.11.1/dist/sass.sync.js';

// Lazy-loaded Sass instance
let sassInstance: any = null;
let sassLoadPromise: Promise<any> | null = null;

/**
 * Lazy load Sass.js from CDN
 */
const loadSass = async (): Promise<any> => {
  if (sassInstance) return sassInstance;

  if (sassLoadPromise) return sassLoadPromise;

  sassLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SASS_CDN;
    script.async = true;

    script.onload = () => {
      sassInstance = (window as any).Sass;
      resolve(sassInstance);
    };

    script.onerror = () => {
      sassLoadPromise = null;
      reject(new Error('Failed to load Sass.js'));
    };

    document.head.appendChild(script);
  });

  return sassLoadPromise;
};

/**
 * Compile SCSS to CSS using Sass.js
 */
export const compileSCSS = async (scss: string): Promise<{ css: string; error?: string }> => {
  if (!scss.trim()) {
    return { css: '' };
  }

  try {
    const Sass = await loadSass();

    return new Promise((resolve) => {
      Sass.compile(scss, (result: any) => {
        if (result.status === 0) {
          resolve({ css: result.text });
        } else {
          resolve({ css: scss, error: result.message || 'SCSS compilation error' });
        }
      });
    });
  } catch (error) {
    return { css: scss, error: 'Failed to load SCSS compiler' };
  }
};

/**
 * Process CSS with autoprefixer-like transformations
 * Note: This is a simplified PostCSS-like processor without full PostCSS
 */
export const processPostCSS = async (css: string): Promise<{ css: string; error?: string }> => {
  if (!css.trim()) {
    return { css: '' };
  }

  try {
    // Simple autoprefixer for common properties
    let processed = css;

    // Add webkit prefixes for common properties
    const prefixRules = [
      { prop: 'transform', prefix: '-webkit-transform' },
      { prop: 'transition', prefix: '-webkit-transition' },
      { prop: 'animation', prefix: '-webkit-animation' },
      { prop: 'flex', prefix: '-webkit-flex' },
      { prop: 'flex-direction', prefix: '-webkit-flex-direction' },
      { prop: 'justify-content', prefix: '-webkit-justify-content' },
      { prop: 'align-items', prefix: '-webkit-align-items' },
      { prop: 'user-select', prefix: '-webkit-user-select' },
      { prop: 'backdrop-filter', prefix: '-webkit-backdrop-filter' },
      { prop: 'appearance', prefix: '-webkit-appearance' },
    ];

    prefixRules.forEach(({ prop, prefix }) => {
      // Match property declarations that don't already have prefixed versions
      const regex = new RegExp(`(^|[{;\\s])${prop}\\s*:`, 'gm');
      processed = processed.replace(regex, (match) => {
        // Add the prefixed version before the original
        const leadingChar = match[0];
        const propDecl = match.slice(1);
        return `${leadingChar}${prefix}:${propDecl.split(':')[1] || ''};\n  ${propDecl}`;
      });
    });

    // Simple nesting support (CSS nesting is now standard but may need fallback)
    // This is a basic implementation - full PostCSS nesting would be more complex
    // For now, we'll just pass through and note that modern browsers support nesting

    return { css: processed };
  } catch (error) {
    return { css, error: 'PostCSS processing error' };
  }
};

/**
 * Simple hash function for generating unique class suffixes
 */
const simpleHash = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36).slice(0, 6);
};

/**
 * Process CSS Modules - scope class names to prevent conflicts
 */
export const processCSSModules = (
  css: string,
  html: string
): { css: string; html: string; classMap?: Record<string, string>; error?: string } => {
  if (!css.trim()) {
    return { css: '', html };
  }

  try {
    // Generate a deterministic hash based on CSS content only
    // (Date.now() would cause class names to change on every preview update)
    const moduleHash = simpleHash(css);

    // Find all class selectors in CSS
    const classRegex = /\.([a-zA-Z_][\w-]*)/g;
    const classMap = new Map<string, string>();

    // First pass: collect all class names
    let match;
    while ((match = classRegex.exec(css)) !== null) {
      const className = match[1];
      if (!classMap.has(className)) {
        classMap.set(className, `${className}_${moduleHash}`);
      }
    }

    // Second pass: replace class names in CSS
    let processedCSS = css;
    classMap.forEach((scopedName, originalName) => {
      const cssClassRegex = new RegExp(`\\.${originalName}(?![\\w-])`, 'g');
      processedCSS = processedCSS.replace(cssClassRegex, `.${scopedName}`);
    });

    // Third pass: replace class names in HTML
    let processedHTML = html;
    classMap.forEach((scopedName, originalName) => {
      // Match class="..." or class='...' attributes
      const htmlClassRegex = new RegExp(
        `(class=["'][^"']*\\b)${originalName}(\\b[^"']*["'])`,
        'g'
      );
      processedHTML = processedHTML.replace(htmlClassRegex, `$1${scopedName}$2`);
    });

    const classMapObj: Record<string, string> = {};
    classMap.forEach((scopedName, originalName) => {
      classMapObj[originalName] = scopedName;
    });

    return { css: processedCSS, html: processedHTML, classMap: classMapObj };
  } catch (error) {
    return { css, html, error: 'CSS Modules processing error' };
  }
};

/**
 * Main CSS processor function that routes to the appropriate processor
 */
export const processCSS = async (
  css: string,
  html: string,
  mode: TCSSMode
): Promise<{ css: string; html: string; classMap?: Record<string, string>; error?: string }> => {
  switch (mode) {
    case 'scss': {
      const result = await compileSCSS(css);
      return { css: result.css, html, error: result.error };
    }
    case 'postcss': {
      const result = await processPostCSS(css);
      return { css: result.css, html, error: result.error };
    }
    case 'modules': {
      return processCSSModules(css, html);
    }
    case 'css':
    default:
      return { css, html };
  }
};
